import { ResourceHelperService } from "../resource-helper.service";

/**
 * A dataservice that performs operations on payments or usagePatterns entries against the backend Payment and UsagePatterns service
 */
export class PaymentService {
  public paymentRequestResource: InterACT.Interfaces.IResource<InterACT.Interfaces.Payments.PaymentRequestResponse | InterACT.Interfaces.Payments.PaymentRequestsResponse>;
  public usagePatternResource: InterACT.Interfaces.IResource<InterACT.Interfaces.UsagePatterns.UsagePatternResponse | InterACT.Interfaces.UsagePatterns.UsagePatternsResponse>;
  public paymentTokensResource: InterACT.Interfaces.IResource<InterACT.Interfaces.Payments.PaymentTokensResponse>;
  public paymentTokenResource: InterACT.Interfaces.IResource<InterACT.Interfaces.Payments.PaymentTokenResponse>;
  public paymentSummaryResource: InterACT.Interfaces.IResource<InterACT.Interfaces.Journeys.SummationResponse>;
  public paymentRequestExternalRefundResource: InterACT.Interfaces.IResource<InterACT.Interfaces.Payments.PaymentRequestRefundResponse>;

  constructor(private $resource, private resourceHelperService: ResourceHelperService) {
    this.paymentRequestResource = $resource('api/payment-requests/:paymentRequestId');
    this.usagePatternResource = $resource('api/usage-patterns');
    this.paymentTokensResource = $resource('api/payment-tokens');
    this.paymentTokenResource = $resource('api/payment-tokens/:tokenId');
    this.paymentSummaryResource = $resource('api/payment-requests/summation/');
    this.paymentRequestExternalRefundResource = $resource('api/payment-request-refunds/:id');
  }

  /**
   * Retrieves list of Payment Transaction
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The string to search while querying payment request
   *
   * @returns A promise of a IPaymentRequestResponse
   */
  public getPaymentHistory(page: number, perPageCount: number, searchText: string) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount, searchText);
    return <ng.IPromise<InterACT.Interfaces.Payments.PaymentRequestsResponse>>this.paymentRequestResource.get(options).$promise;
  }

  /**
   * Retrieves list of Usage Patterns
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The string to search while querying Usage Patterns
   *
   * @returns A promise of a UsagePatternServiceResponse
   */
  public getUsagePattern(page: number, perPageCount: number, searchText: string) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount, searchText);
    return this.usagePatternResource.get(options).$promise;
  }

  /**
 * Retrieves a payment token with a given id
 *
 * @param id The id of the payment token to retrieve
 * @returns A promise of a IPaymentTokensResponse
 */
  public getPaymentToken(id: string): ng.IPromise<InterACT.Interfaces.Payments.PaymentTokenResponse> {
    return this.paymentTokenResource.get({ tokenId: id }).$promise;
  }

  /**
   * Retrieves list of payment tokens
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The string to search while querying payment tokens
   *
   * @returns A promise of a IPaymentTokensResponse
   */
  public getPaymentTokens(page: number, perPageCount: number, searchText: string) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount, searchText);
    return this.paymentTokensResource.get(options).$promise.then((response) => {
      return response.paymentTokens;
    });
  }

  /**
  * Retrieves Total Payment Amount
  *
  * @param page The number of the page of data to be retrieved
  * @param perPageCount The number of items per page to be retrieved
  * @param queryString The query to run against the Payment Amount
  *
  * @returns A promise of a IPaymentSummaryServiceResponse
  */
  public getPaymentSummary(page: number, perPageCount: number, searchText: string, groupBy: string) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount, searchText, undefined, groupBy);
    return this.paymentSummaryResource.get(options).$promise.then((response) => {
      return response.summary;
    });
  }

  public getPaymentRequest = (requestId: string, includeMeta?: boolean) => {
    return this.paymentRequestResource.get({ paymentRequestId: requestId }).$promise.then((resource: InterACT.Interfaces.Payments.PaymentRequestResponse) => {
      if (includeMeta) {
        return resource;
      }

      return resource.paymentRequest;
    });
  }

  /**
  * Retrieves a payment request refund with a given id
  *
  * @param id The id of the payment request refund to retrieve
  * @returns A promise that resolves with a payment request refund
  */
  public getPaymentRequestRefund(id: string) {
    return this.paymentRequestExternalRefundResource.get({ id: id }).$promise;
  }

  /**
  * Creates a payment request refund
  *
  * @param paymentRequestRefund The payment request refund to create
  * @returns A promise that resolves with a payment request refund
  */
 public createPaymentRequestRefund(paymentRequestRefund: InterACT.Interfaces.Payments.PaymentRequestRefund) {
  return this.paymentRequestExternalRefundResource.save(paymentRequestRefund).$promise;
}
}